import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "disabled", "error", "fullWidth", "id", "inputRef", "noResize", "label", "helpMessage", "errorMessage", "tooltip", "tooltipPlacement", "dangerouslyForceFocusStyling", "dangerouslyForceHoverStyling"];
import React from 'react';
import classNames from 'classnames';
import { useId } from '@nerdwallet/react-accessibility';
import { ActionBlock } from '../ActionBlock/ActionBlock';
import { InputGroup, shouldRenderInputGroup } from '../InputGroup/InputGroup';
import styles from './Textarea.module.less';

/**
 * `Textarea` is an input that allows the user to enter long form info and manually adjust the
 * input size accordingly.
 *
 * This component is a stateless visual wrapper on top of the HTML `<textarea>` and takes many of
 * the same props.
 */
export var Textarea = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames;

  // Determine if we should compose an Input
  // within an InputGroup
  var willRenderInputGroup = shouldRenderInputGroup(props);

  var className = props.className,
      disabled = props.disabled,
      error = props.error,
      fullWidth = props.fullWidth,
      idProp = props.id,
      inputRef = props.inputRef,
      noResize = props.noResize,
      label = props.label,
      helpMessage = props.helpMessage,
      errorMessage = props.errorMessage,
      tooltip = props.tooltip,
      tooltipPlacement = props.tooltipPlacement,
      dangerouslyForceFocusStyling = props.dangerouslyForceFocusStyling,
      dangerouslyForceHoverStyling = props.dangerouslyForceHoverStyling,
      otherProps = _objectWithoutProperties(props, _excluded);

  var id = useId(idProp);
  var errorMessageId = "".concat(id, "-error-message");
  var helpMessageId = "".concat(id, "-help-message");
  var ariaDescribedByIds = "".concat(errorMessageId, " ").concat(helpMessageId); // We can only attach an `aria-describedby` attribute if at least one of
  // the things it references is actually being displayed.
  // See https://nerdwallet.atlassian.net/browse/FEI-2206

  var renderAriaDescribedBy = helpMessage || errorMessage && error;
  var textarea = /*#__PURE__*/React.createElement(ActionBlock, _extends({
    "aria-invalid": error ? 'true' : undefined,
    "aria-describedby": renderAriaDescribedBy ? ariaDescribedByIds : undefined,
    component: "textarea",
    disabled: disabled,
    "data-currency": "TextArea",
    error: error,
    className: classNames(styles.textarea, (_classNames = {}, _defineProperty(_classNames, styles.fullWidth, fullWidth), _defineProperty(_classNames, styles.noResize, noResize), _classNames), // If rendering with an InputGroup, the className will apply to the
    // InputGroup instead of the textarea itself.
    !willRenderInputGroup && className) // `inputRef` exists mostly for backwards compatibility
    ,
    ref: inputRef || ref,
    id: id,
    dangerouslyForceFocusStyling: dangerouslyForceFocusStyling,
    dangerouslyForceHoverStyling: dangerouslyForceHoverStyling
  }, otherProps));

  if (!willRenderInputGroup) {
    return textarea;
  }

  return /*#__PURE__*/React.createElement(InputGroup, {
    className: className,
    disabled: disabled,
    error: error,
    errorMessage: errorMessage,
    errorMessageId: errorMessageId,
    helpMessage: helpMessage,
    helpMessageId: helpMessageId,
    input: textarea,
    inputId: id,
    label: label,
    tooltip: tooltip,
    tooltipPlacement: tooltipPlacement
  });
});
Textarea.displayName = 'Textarea';